import { Product, Sku } from '@v2/shared/lib/types';

/**
 * Everything the shipping api needs about where a parcel is going. The wallets hand
 * back rather less than a checkout form does — no street, no name — which is fine,
 * because at this point the question is only what shipping and tax cost.
 */
export type PaymentAddress = {
  city: string;
  state: string;
  zip: string;
  /** ISO-2, upper case. */
  country_iso2: string;
};

export type EstimatedDelivery = {
  minimum_number_of_days: number;
  maximum_number_of_days: number;
};

export type ShippingOption = {
  id: string;
  name: string;
  cost: number;
  estimated_delivery: EstimatedDelivery;
};

export type ShippingCalculationResult = {
  shipping_options: ShippingOption[];
  total_cost: number;
};

export type TaxCalculationResult = {
  total: number;
};

export type PaymentCartItem = {
  product: Pick<Product, 'id'>;
  sku: Pick<Sku, 'code'>;
  quantity: number;
  price: number;
  discount: number;
};

/**
 * The order being paid for, as the wallet sheets see it.
 *
 * Not a `Cart`, and deliberately not tied to whether one exists. On a product page
 * nothing has been added to the visitor's real cart yet, so this is a *proposed* order
 * assembled from the selected format; in the sale page's drawer it is the real cart,
 * restated in the shape the sheets price. Either way it is what the sheet's line items
 * and running total are computed from, and the wallets do not know which they were given.
 *
 * The three totals are updated as the visitor picks an address and a shipping method,
 * which is why they travel together rather than being derived at each call site.
 */
export type PaymentCart = {
  items: PaymentCartItem[];
  total_items_price: number;
  total_discounts: number;
  shipping_total_amount: number;
  tax_total_amount: number;
  address?: PaymentAddress;
  shipping_calculation_result?: ShippingCalculationResult;
  selected_shipping_option?: ShippingOption;
};

/**
 * What happens once a sheet comes back authorized, given the tokenized payload.
 *
 * A function rather than the wallet calling the cart itself, because the two placements
 * finish in genuinely different ways and only the caller knows which it is. A product
 * page puts the selected sku in the cart with the payload hanging off it and follows the
 * cart's `checkout_url`; the sale page's drawer has a cart already, and adding to it with
 * a payload would empty it first — so it posts the payload straight to that same
 * `cart?xCmd=checkout`. The wallets are indifferent to both.
 */
export type SubmitPayload = (paymentPayload: string) => Promise<void>;

/**
 * The corner a row of wallet buttons is drawn with.
 *
 * Named once and translated by each button, because the three sdks take it in three
 * different units — a tailwind class for the one we draw ourselves, a radius in pixels for
 * Google's, one of PayPal's own shape names for PayPal's. A row that spelled it out per
 * button is a row whose buttons drift apart, which is exactly what happened here.
 *
 * `pill` is the product page's row, matching the Add to Cart button beside it. `rounded` is
 * the cart's, and it is Tailwind's default 4px radius because that is what all four of the
 * legacy cart's buttons are: bootstrap's `.btn` at `.25rem`, `buttonRadius: 4` on its
 * Google Pay button (`src/DMagzWeb/shared/components/google_pay/button.js`) and PayPal's
 * `rect`.
 */
export type WalletShape = 'pill' | 'rounded';

/**
 * The same shapes as tailwind classes, for whatever sits beside a wallet row and has to
 * agree with it — the sale cart's Checkout button, which is neither a wallet nor an sdk.
 */
export const WALLET_SHAPE_CLASSES: Record<WalletShape, string> = {
  pill: 'rounded-full',
  rounded: 'rounded',
};
